import {
  AlertCircle,
  Bluetooth,
  Calendar,
  CheckCircle,
  Code,
  Cpu,
  FileText,
  Globe,
  Layers,
  Layout,
  LifeBuoy,
  MapPin,
  PenTool,
  PlaySquare,
  RefreshCw,
  Search,
  Server,
  Shield,
  ShieldCheck,
  ShoppingBag,
  Smartphone,
  Target,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react";

import type { AboutUsCopy } from "@components/bluetooth-landing/AboutUs";
import type { FAQCopy } from "@components/bluetooth-landing/FAQ";
import type { FooterCopy } from "@components/bluetooth-landing/Footer";
import type { HeroCopy } from "@components/bluetooth-landing/Hero";
import type { NavbarCopy } from "@components/bluetooth-landing/Navbar";
import type { ProcessCopy } from "@components/bluetooth-landing/Process";
import type { ServicesCopy } from "@components/bluetooth-landing/Services";
import type { TargetAudienceCopy } from "@components/bluetooth-landing/TargetAudience";
import type { ValuePropositionCopy } from "@components/bluetooth-landing/ValueProposition";
import { faqsEn } from "@data/bluetooth-landing/faq";

export const title = "Mobile Apps with Bluetooth";
export const description =
  "We build stable mobile apps for Bluetooth LE devices for the European market. We help manufacturers and importers move from prototype to iOS and Android apps.";

export const structuredData = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Blues Brackets",
    "url": "https://bluesbrackets.com",
    "logo": "https://bluesbrackets.com/favicon-dark.png",
    description,
    "areaServed": "EU",
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "sales",
      "email": "hello@bluesbrackets.com",
      "availableLanguage": ["Polish", "English"],
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Bluetooth mobile app development",
    "provider": {
      "@type": "Organization",
      "name": "Blues Brackets",
    },
    description,
    "areaServed": "EU",
    "serviceType": "Mobile app development",
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqsEn.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a,
      },
    })),
  },
];

export const navbarCopy: NavbarCopy = {
  links: {
    targetAudience: "Who it is for",
    values: "Value",
    services: "Services",
    process: "Process",
    about: "About us",
    faq: "FAQ",
  },
  cta: "Estimate your project",
};

export const servicesCopy: ServicesCopy = {
  header: {
    h2: "Services",
    h3: "Mobile apps from Bluetooth to UX",
    p: "From early sketches and analysis to launch and production support. We guide you through the full product lifecycle.",
  },
  services: [
    {
      Icon: PenTool,
      title: "Product workshops",
      description:
        "We organize your requirements into a professional brief that becomes the basis for delivery. We identify key features and split the project into clear stages.",
    },
    {
      Icon: Search,
      title: "Device and documentation analysis",
      description:
        "If the supplier cannot provide full documentation, we analyze how the device works. We learn the hardware limitations and reduce risk before development starts.",
    },
    {
      Icon: Layers,
      title: "UX/UI design",
      description:
        "We design an intuitive interface that makes the device easier to use. We prepare mockups focused on usability, brand consistency, and implementation readiness.",
    },
    {
      Icon: Smartphone,
      title: "iOS and Android app development",
      description:
        "We build one cross-platform app. We implement Bluetooth communication, app logic, accounts, and backend integration, then test real scenarios on physical hardware.",
    },
    {
      Icon: Server,
      title: "Backend infrastructure",
      description:
        "We build backend services and integrate them with your infrastructure. We can provide an admin panel, monitoring, analytics, and account management.",
    },
    {
      Icon: PlaySquare,
      title: "Store release",
      description:
        "We prepare the app for the Apple App Store and Google Play. We help with formal requirements, developer accounts, descriptions, and privacy policies.",
    },
    {
      Icon: LifeBuoy,
      title: "Post-launch support",
      description:
        "We maintain the app, monitor performance, and respond to issues. We provide updates for new iOS and Android versions and continue developing the app as needed.",
    },
  ],
  cta: {
    h4: "Need support?",
    p: "Tell us about your project. We will help define the right scope and guide you through the process.",
    button: {
      text: "Book a free consultation",
      href: "#kontakt",
    },
  },
};

export const heroCopy: HeroCopy = {
  header: "We build mobile apps for",
  headerTail: "Bluetooth devices",
  description:
    "Stable, intuitive, and ready for the European market. We help manufacturers and importers move from prototype to iOS and Android apps.",
  cta: {
    text: "Free consultation",
    href: "#kontakt",
  },
  secondaryCta: {
    text: "See our process",
    href: "#proces",
  },
  benefits: [
    { text: "Polish team", Icon: CheckCircle },
    { text: "Ready for GDPR and the EU market", Icon: CheckCircle },
    { text: "Support before and after launch", Icon: CheckCircle },
  ],
};

export const targetAudienceCopy: TargetAudienceCopy = {
  header: {
    h2: "Who we work with",
    h3: "Who is this offer for?",
    p: "We help companies that want to create or improve the user experience around Bluetooth hardware. Does this sound like your project?",
  },
  points: [
    {
      Icon: Cpu,
      title: "You are a manufacturer or importer",
      description:
        "You sell Bluetooth LE devices and need a trusted partner to build the software around them.",
    },
    {
      Icon: Target,
      title: "You already have working hardware",
      description:
        "Your device is ready, but you need a dedicated mobile app to unlock its full potential.",
    },
    {
      Icon: AlertCircle,
      title: "Your current app is underperforming",
      description:
        "It looks dated, is unstable, loses connection, or is simply hard for end users to operate.",
    },
    {
      Icon: RefreshCw,
      title: "Documentation is incomplete",
      description:
        "You have a Bluetooth LE device but lack full documentation. We can recover it through reverse engineering.",
    },
    {
      Icon: Users,
      title: "You need a new customer channel",
      description:
        "You want the app to support long-term customer relationships and collect useful analytics.",
    },
  ],
  cta: {
    h4: "Sounds familiar?",
    text: "Let us talk about how we can solve the challenges in your project.",
    href: "#kontakt",
    buttonText: "Book a free consultation",
  },
};

export const valuePropositionCopy: ValuePropositionCopy = {
  header: {
    h2: "Value proposition",
    h3: "Why trust us with your Bluetooth app?",
    p: "We combine mobile development, wireless technology, and design. We do not learn on your project. We bring proven practices from day one.",
    imageAlt: "Software development and code",
  },
  values: [
    {
      Icon: Bluetooth,
      title: "Bluetooth is our specialty",
      description:
        "We have hands-on experience with Bluetooth Low Energy (BLE) and Auracast devices. We know their capabilities, limitations, testing needs, and the issues that appear most often.",
    },
    {
      Icon: Shield,
      title: "A technical expert on your side",
      description:
        "We support technical conversations with the manufacturer, help complete or recover documentation, and advise how to reduce risks from the start.",
    },
    {
      Icon: Layout,
      title: "UX comes first",
      description:
        "A good user experience is essential for a successful app. Our UX designer helps simplify device operation and make the product pleasant to use every day.",
    },
    {
      Icon: TrendingUp,
      title: "An app ready to grow",
      description:
        "Your app can become an ecosystem for new products, analytics, and upselling. You learn more about your customers, build loyalty, and keep pace with business growth.",
    },
    {
      Icon: Globe,
      title: "Built for European users",
      description:
        "We care about translation quality, accessibility, data security, and GDPR compliance. User data is stored in the EU under the care of Polish specialists.",
    },
  ],
};

export const processCopy: ProcessCopy = {
  header: {
    h2: "Our process",
    h3: "How your app is built",
    p: "Clear cooperation rules, defined stages, and ongoing communication. See how we work from the first email to launch.",
  },
  stepNumber: "Step",
  steps: [
    {
      Icon: Calendar,
      title: "Free online meeting",
      duration: "within a few hours",
      description:
        "You get a 30-minute call with an expert and a summary with an initial project estimate.",
    },
    {
      Icon: FileText,
      title: "Product workshops",
      duration: "within a few days",
      description:
        "After a 2-hour online workshop, you receive a PDF report, a detailed estimate, and a delivery proposal.",
    },
    {
      Icon: Search,
      title: "Hardware sample analysis",
      duration: "1-2 weeks",
      description:
        "You receive documented communication test results and confidence that the device meets expectations and can support the project.",
    },
    {
      Icon: PenTool,
      title: "Contract signing",
      duration: "schedule agreement",
      description:
        "We agree on the project start and end dates, with a clear commitment to the timeline.",
    },
    {
      Icon: Code,
      title: "Stage-by-stage delivery",
      duration: "min. 2-3 months",
      description:
        "Mockups, development, and testing. Your app takes shape while you follow progress and test demo versions.",
    },
    {
      Icon: ShoppingBag,
      title: "Store release",
      duration: "min. 2 weeks",
      description:
        "We handle the Apple and Google review process and publish the app in the App Store and Google Play.",
    },
    {
      Icon: CheckCircle,
      title: "Handover and support",
      duration: "ongoing",
      description:
        "You receive the source code, or we start a continuous support, hosting, and maintenance service.",
    },
    {
      Icon: Zap,
      title: "Further development",
      duration: "future versions",
      description:
        "The app is ready for analytics, new marketing features, additional products, and ongoing growth.",
    },
  ],
  cta: {
    href: "#kontakt",
    text: "Start building your app",
  },
};

export const aboutUsCopy: AboutUsCopy = {
  header: {
    h2: "About us",
    h3: "A local team with European standards",
    p1: "We are a small team from Poland combining experience in mobile apps, Bluetooth, and embedded systems.",
    p2: "Michal, Tomasz, and Kuba take care of implementation and code stability after launch, while Mateusz designs UX that makes the product easier to use every day.",
    p3: "GDPR compliant",
    p4: "Data in the EU",
    Icon: ShieldCheck,
    imageAlt: "Team working on an app in the office",
  },
  values: [
    {
      Icon: Users,
      title: "You work directly with us",
      description:
        "You get a technical partner without unnecessary intermediaries.",
    },
    {
      Icon: MapPin,
      title: "We understand the European market",
      description:
        "We know how to launch a product in the EU and meet GDPR requirements.",
    },
    {
      Icon: ShieldCheck,
      title: "We care about data security",
      description:
        "All user data is stored in the EU under the care of Polish specialists.",
    },
  ],
  cta: {
    text: "Contact us",
    href: "#kontakt",
  },
};

export const faqCopy: FAQCopy = {
  header: {
    h2: "FAQ",
    h3: "Frequently asked questions",
  },
  faqs: faqsEn,
};

export const footerCopy: FooterCopy = {
  header: {
    h2: "Looking for a technology partner?",
    p: "You are in the right place. Book a free consultation, tell us about your device, and get an initial estimate for your project.",
  },
  cta: {
    href: "#kontakt",
    text: "Book a free consultation",
    p: "We build stable mobile apps for Bluetooth LE devices for the European market. We support you from concept to launch and maintenance.",
    email: "hello@bluesbrackets.com",
    phone: "+48 535 462 678",
  },
  links: {
    h4: "Quick links",
    links: [
      { href: "#dla-kogo", text: "Who we work with" },
      { href: "#uslugi", text: "Services" },
      { href: "#proces", text: "Process" },
      { href: "#faq", text: "FAQ" },
      { href: "/ibeacon-app-development/", text: "iBeacon App Development" },
    ],
  },
  knowledgeBase: {
    h4: "Knowledge base",
    links: [
      {
        href: "/blogs/is_bluetooth_le_right_for_your_product",
        text: "Is Bluetooth LE the right technology for your product or MVP?",
      },
      {
        href: "/blogs/bluetooth_le_product_architecture",
        text: "Bluetooth LE IoT product architecture and development",
      },
      {
        href: "/blogs/ble_mobile_application_support",
        text: "BLE support on the mobile application side",
      },
      {
        href: "/blogs/ble_embedded_development_platforms",
        text: "Best platforms for Bluetooth LE product development",
      },
      {
        href: "/blogs/auracast_mcu",
        text: "ESP32 Auracast: Does It Work? Best MCUs for LE Audio",
      },
      {
        href: "/blogs/ble_hardware_2026",
        text: "Best hardware for Bluetooth Low Energy BLE development in 2026",
      },
    ],
  },
  copyright: "bluesBrackets. All rights reserved.",
  privacyPolicy: "Privacy Policy",
  privacyPolicyHref: "/privacy-policy",
  copiedLabel: "Copied to clipboard",
  contactForm: {
    heading: "Contact us",
    fields: {
      name: {
        label: "Full name",
        placeholder: "John Smith",
        requiredError: "Enter your full name",
      },
      email: {
        label: "Email address",
        placeholder: "john@company.com",
        requiredError: "Enter your email address",
        invalidError: "Enter a valid email address",
      },
      topic: {
        label: "Project description",
        placeholder: "Briefly describe your project and current challenges...",
        requiredError: "Describe your project",
        maxLengthError: "The description can be up to 2000 characters long",
      },
    },
    submit: "Send inquiry",
    submitting: "Sending...",
    successMessage: "Message sent!",
    errorMessage: "Could not send the message :(",
    responseTime: "We reply within 24 hours on business days.",
    emailSubjectPrefix: "Full name",
  },
};
